import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Smartphone, Loader2, ChevronLeft, Circle, Square, MessageCircle, Maximize2, Minimize2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import StreamStateChip, { useStreamStatus } from '../StreamStateChip';
import { streamManager, attachVisibilityAutostop } from '../../lib/streamManager';
import { WSSCRCPY_BASE, SPRING_API_BASE, isWebCodecsSupported } from '../../lib/scrcpyStream';
import { resolveMonitorConfig, probeLocalRelay, CONFIG_TIMEOUT_MS } from '../../lib/monitorConfig';
import { bmPresenceApi, bmVideoApi, devicesApi } from '../../lib/api';
import { resolveChatPhone, phoneChoices, phoneRoute, getPickedPhone, setPickedPhone } from '../../lib/chatPhone';
import { videoPoint, gesture } from '../../lib/chatLive';

/*
 * r16q6 (Live mode, docs/LIVE-MODE-DESIGN.md section 8): the phone's live screen beside the chat. It reuses the Phone Monitor's stream and
 * state chip, so it behaves the same: one socket while it's shown, frozen while the tab is hidden, a plain reason when there's no picture.
 * It goes through this PC's relay when that answers, else through the server to the PC reporting the phone; the server checks the install
 * is this account's. The worker isn't involved, so stopping a worker doesn't touch the picture.
 *
 * r16q8 (decision D1): while Live is on, a tap or swipe on the picture goes to the phone. It travels through the server and the Live worker, never the video's own link, which only ever
 * carries quality settings. So it works on either route and takes a moment; the dot marks where it went.
 */
const WEBCODECS_OK = isWebCodecsSupported();

function Note({ children, testid }) {
  return <p className="px-4 py-8 text-center text-sm text-muted-foreground" data-testid={testid}>{children}</p>;
}

/* r16q11: "Streaming" sits by the phone's name, not over its screen. Any other state still shows on the picture, where it explains what's wrong with it. */
function StreamingWord({ streamKey }) {
  const st = useStreamStatus(streamKey);
  if (!st || st.state !== 'streaming') return null;
  return (
    <span className="inline-flex shrink-0 items-center gap-1 text-[11px] text-success" data-testid="phone-streaming">
      <span className="h-1.5 w-1.5 rounded-full bg-success" aria-hidden />Streaming
    </span>
  );
}

const NAV_BUTTON = 'tap inline-flex h-8 w-9 shrink-0 items-center justify-center rounded-lg border border-border bg-secondary/60 text-foreground hover:bg-secondary';

export default function PhonePane({ slotName, slots, className, live: liveMode }) {
  // r16q11: bigger, over the side of the page: the picture's height is what limits how readable its text is
  const [big, setBig] = useState(false);
  const [picked, setPicked] = useState(() => (slotName ? getPickedPhone(slotName) : null));
  useEffect(() => { setPicked(slotName ? getPickedPhone(slotName) : null); }, [slotName]);

  const devicesQ = useQuery({ queryKey: ['devices'], queryFn: () => devicesApi.list().then((r) => r.data), staleTime: 300000 });
  const presenceQ = useQuery({ queryKey: ['bm-presence'], queryFn: () => bmPresenceApi.list().then((r) => r.data), refetchInterval: 10000 });
  const phone = useMemo(() => resolveChatPhone({
    slotName, slots, devices: devicesQ.data, presence: presenceQ.data, pickedId: picked,
  }), [slotName, slots, devicesQ.data, presenceQ.data, picked]);

  // ---- the route, decided once per phone and PC: nothing opens before it's known
  const [route, setRoute] = useState({ forKey: null, value: null, cfg: null });
  const serial = phone.serial || null;
  const installId = phone.status === 'ok' ? phone.installId : null;
  const key = phone.status === 'ok' ? phone.key : null;
  useEffect(() => {
    if (!key || !WEBCODECS_OK) return undefined;
    let gone = false;
    (async () => {
      let cfg = {};
      try {
        cfg = await resolveMonitorConfig({
          search: '', fetchServerConfig: () => bmVideoApi.config(CONFIG_TIMEOUT_MS), envTransport: undefined, nodeEnv: process.env.NODE_ENV,
        });
      } catch (e) {
        cfg = {};
      }
      const probe = await probeLocalRelay({ base: WSSCRCPY_BASE, timeoutMs: cfg.autoProbeMs || 1000 }).catch(() => ({ ok: false }));
      if (!gone) setRoute({ forKey: key, value: phoneRoute(!!(probe && probe.ok), installId), cfg });
    })();
    return () => { gone = true; };
  }, [key, installId]);

  // ---- the stream
  const entriesRef = useRef([]);
  const optsRef = useRef({});
  const hostRef = useRef(null);
  const [tick, setTick] = useState(0);
  const live = route.forKey === key && route.value ? route.value : null;
  useEffect(() => {
    if (!key || !live || !serial) return undefined;
    const cfg = route.cfg || {};
    entriesRef.current = [{ key, serial, installId: installId === null ? undefined : installId, pc: phone.pc || '' }];
    optsRef.current = {
      transport: live.transport,
      autoFallback: live.autoFallback,
      apiBase: SPRING_API_BASE,
      reconnect: true,
      remoteLow: cfg.remoteLow,
      remoteHigh: cfg.remoteHigh,
      stagger: false,
      hold: !!cfg.hold,
      reprobe: false,
    };
    streamManager.syncPage(entriesRef.current, WSSCRCPY_BASE, optsRef.current);
    streamManager.setQuality(key, 'sharp');	// one phone on the page, and its text has to be read
    setTick((t) => t + 1);
    const detach = attachVisibilityAutostop(streamManager, () => entriesRef.current, {
      base: WSSCRCPY_BASE,
      getSyncOpts: () => optsRef.current,
      canResume: () => true,
      debounceMs: 400,
      onChange: () => setTick((t) => t + 1),
    });
    streamManager.configure({ onSwap: () => setTick((t) => t + 1) });
    return () => {
      detach();
      streamManager.configure({ onSwap: null });
      streamManager.stopAll();
    };
    // the phone's name changing on a presence poll must not reopen the stream
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, serial, installId, live && live.transport, live && live.autoFallback]);

  useEffect(() => {
    const host = hostRef.current;
    if (!host || !key) return undefined;
    const canvas = streamManager.getCanvas(key);
    if (canvas) host.appendChild(canvas);
    return () => {
      const c = streamManager.getCanvas(key);
      if (c && c.parentNode === host) host.removeChild(c);
    };
  }, [key, tick]);

  // ---- r16q8: taps on the picture during Live
  const canTap = !!(liveMode && liveMode.ready && liveMode.taps);
  // r16q10: a touch on the picture before Live is on did nothing and said nothing: now it says why, and offers Live
  const liveOff = !!(liveMode && liveMode.enabled && liveMode.taps && !liveMode.ready);
  const [touchedOff, setTouchedOff] = useState(false);
  useEffect(() => { if (!liveOff) setTouchedOff(false); }, [liveOff]);
  const press = useRef(null);
  const [dot, setDot] = useState(null);
  const pointOn = (e) => {
    const canvas = key ? streamManager.getCanvas(key) : null;
    return videoPoint(e.clientX, e.clientY, e.currentTarget.getBoundingClientRect(), canvas && canvas.width, canvas && canvas.height);
  };
  const onDown = (e) => {
    press.current = { at: pointOn(e), cx: e.clientX, cy: e.clientY, t: Date.now() };
    if (e.currentTarget.setPointerCapture) e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onUp = (e) => {
    const p = press.current;
    press.current = null;
    if (!p) return;
    const what = gesture(p.at, pointOn(e), Math.hypot(e.clientX - p.cx, e.clientY - p.cy), Date.now() - p.t);
    if (!what) return;
    const box = e.currentTarget.getBoundingClientRect();
    setDot({ x: p.cx - box.left, y: p.cy - box.top, n: Date.now() });
    liveMode.input(what);
  };
  useEffect(() => {
    if (!dot) return undefined;
    const t = setTimeout(() => setDot(null), 900);
    return () => clearTimeout(t);
  }, [dot]);

  const choices = phoneChoices(devicesQ.data);
  const pick = (id) => {
    if (slotName) setPickedPhone(slotName, id);
    setPicked(id);
  };

  let body;
  if (!WEBCODECS_OK) {
    body = <Note testid="phone-no-webcodecs">This browser can&apos;t show the phone&apos;s video. Use Chrome or Edge.</Note>;
  } else if (phone.status === 'loading') {
    body = <div className="flex flex-1 items-center justify-center" aria-busy="true" data-testid="phone-loading"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" aria-hidden /></div>;
  } else if (phone.status === 'none') {
    body = (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 px-4 py-6 text-center" data-testid="phone-none">
        <p className="text-sm text-muted-foreground">
          {slotName ? `${slotName} has no phone assigned.` : 'Pick a chat to see its phone.'}
        </p>
        {slotName && choices.length > 0 && (
          <label className="flex w-full max-w-xs flex-col gap-1 text-xs text-muted-foreground">
            Show this phone
            <select className="tap rounded-lg border border-border bg-secondary/60 px-2 py-2 text-sm text-foreground" value="" onChange={(e) => e.target.value && pick(e.target.value)} data-testid="phone-pick">
              <option value="">Choose a phone</option>
              {choices.map((c) => <option key={c.id} value={c.id}>{c.label} ({c.id})</option>)}
            </select>
          </label>
        )}
      </div>
    );
  } else if (route.forKey === key && !route.value) {
    body = <Note testid="phone-no-route">No PC is reporting {phone.label} right now. Check that its client is running and the phone is connected.</Note>;
  } else {
    body = (
      <div className="relative min-h-0 flex-1 bg-black" data-testid="phone-video">
        <div ref={hostRef} className="absolute inset-0" />
        {liveOff && <div className="absolute inset-0" onPointerDown={() => setTouchedOff(true)} data-testid="phone-off-layer" />}
        <StreamStateChip streamKey={key} pc={phone.pc || phone.label} hideWhenStreaming />
        {liveOff && touchedOff && (
          <div className="absolute inset-x-2 bottom-2 z-10 rounded-xl border border-border bg-popover/95 p-3 text-xs text-foreground shadow-xl" role="status" data-testid="phone-off-note">
            {liveMode.running ? <p>Live is starting. Touches reach the phone as soon as it&apos;s ready.</p> : (
              <>
                <p>Touches and chats only reach the phone during Live. Live stops all automation on this phone until you end it, or it&apos;s idle for {liveMode.idleMinutes || 10} minutes.</p>
                <button type="button" className="tap mt-2 rounded-lg border border-primary/40 bg-primary/15 px-2.5 py-1.5 font-medium text-primary" disabled={liveMode.busy}
                  onClick={() => { setTouchedOff(false); liveMode.start(); }} data-testid="phone-off-start">
                  Turn on Live
                </button>
              </>
            )}
          </div>
        )}
        {canTap && (
          <div
            className="absolute inset-0 cursor-pointer touch-none"
            onPointerDown={onDown}
            onPointerUp={onUp}
            onPointerCancel={() => { press.current = null; }}
            data-testid="phone-tap-layer"
            aria-label="Tap the phone"
          >
            {dot && <span key={dot.n} className="pointer-events-none absolute h-6 w-6 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-white/40" style={{ left: dot.x, top: dot.y }} data-testid="phone-tap-dot" />}
          </div>
        )}
      </div>
    );
  }

  return (
    <section className={cn(className, big && 'fixed inset-y-3 right-3 z-40 !flex !w-[min(30rem,48vw)] shadow-2xl')} aria-label="Phone" data-testid="inbox-phone-pane" data-big={big ? 'true' : 'false'}>
      <header className="flex min-w-0 shrink-0 items-center gap-2 border-b border-border/60 px-3 py-2.5">
        <Smartphone className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
        <span className="truncate text-sm font-medium" data-testid="phone-label">{phone.status === 'ok' ? phone.label : 'Phone'}</span>
        {key && <StreamingWord streamKey={key} />}
        {phone.status === 'ok' && (
          <span className="ml-auto shrink-0 truncate text-xs text-muted-foreground" data-testid="phone-where">
            {phone.pc || (live && live.transport === 'local' ? 'this PC' : '')}
          </span>
        )}
        <button type="button" className={cn('tap inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-muted-foreground hover:bg-secondary hover:text-foreground', phone.status !== 'ok' && 'ml-auto')}
          onClick={() => setBig((b) => !b)} aria-label={big ? 'Smaller phone' : 'Bigger phone'} title={big ? 'Smaller phone' : 'Bigger phone'} data-testid="phone-big">
          {big ? <Minimize2 className="h-3.5 w-3.5" aria-hidden /> : <Maximize2 className="h-3.5 w-3.5" aria-hidden />}
        </button>
      </header>
      {body}
      {canTap && phone.status === 'ok' && (
        <footer className="flex shrink-0 items-center gap-2 border-t border-border/60 px-3 py-2" data-testid="phone-tap-bar">
          <button type="button" className={NAV_BUTTON} onClick={() => liveMode.input({ kind: 'back' })} aria-label="Back" title="Back" data-testid="phone-back">
            <ChevronLeft className="h-4 w-4" aria-hidden />
          </button>
          <button type="button" className={NAV_BUTTON} onClick={() => liveMode.input({ kind: 'home' })} aria-label="Home" title="Home" data-testid="phone-home">
            <Circle className="h-3.5 w-3.5" aria-hidden />
          </button>
          <button type="button" className={NAV_BUTTON} onClick={() => liveMode.input({ kind: 'recents' })} aria-label="Recent apps" title="Recent apps" data-testid="phone-recents">
            <Square className="h-3 w-3" aria-hidden />
          </button>
          <button type="button" className="tap ml-auto inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-lg border border-border bg-secondary/60 px-2.5 py-1.5 text-xs font-medium" onClick={() => liveMode.input({ kind: 'reopen' })} data-testid="phone-reopen">
            <MessageCircle className="h-3.5 w-3.5" aria-hidden /> Open this chat
          </button>
        </footer>
      )}
    </section>
  );
}
